import fp from "fingerprintjs2";
import { useState, useEffect, useCallback, useContext, useRef } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';
// @mui
import { styled } from '@mui/material/styles';
import {
  Stack, 
  Typography,
  Grid,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

import PdfComp from '../components/pdf-reader/index';

import apiFetch from '../axios/config'

import SignatureButton from '../components/signature-button/index'
import SignatureHeader from "../components/signature-header";
import { AccountContext } from '../account';
import { EntityContext } from '../entity';
// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

function SignaturePage(props) {

  const [envelope, setEnvelope] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fingerprint, setFingerprint] = useState (null)
  const [signatureValitdation, setSignatureValitdation] = useState (false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showNoPositionModal, setShowNoPositionModal] = useState(false)

  const { signerKey } = useParams();
  const navigate = useNavigate();
  const { getSession } = useContext(AccountContext);
  const { setSignaturePosition, signaturePosition } = useContext(EntityContext);
  const getSessionRef = useRef(getSession);

  // Limpar posição ao entrar na página / trocar de documento (evitar cache entre envelopes)
  useEffect(() => {
    setSignaturePosition(null);
  }, [signerKey, setSignaturePosition]);
  
  // Atualizar ref quando getSession mudar
  useEffect(() => {
    getSessionRef.current = getSession;
  }, [getSession]);

  const loadEnvelope = useCallback(async () => {
    try {
      const response = await apiFetch.get(
        `/signature/${signerKey}`
      );
  
      const data = response.data;

      setEnvelope(data)
      // CheckSignatureValidation(data.signers)

    } catch (error) {
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || '';
      const isSignatureEndpointError =
        (status === 404 && errorDetail) ||
        (status === 400 && (
          errorDetail.includes('no longer required') ||
          errorDetail.includes('Automatic signature already applied')
        ));
      if (isSignatureEndpointError) {
        navigate('/signature-unavailable', { replace: true, state: { signatureError: errorDetail } });
        return;
      }
      console.log(error);
    } finally {
      setLoading(false);
    }

  }, [signerKey, navigate])

  useEffect(() => {
    loadEnvelope()
  }, [loadEnvelope])

  // Verificar autenticação quando envelope for carregado e is_observer === true
  useEffect(() => {
    if (envelope?.is_observer) {
      getSessionRef.current()
        .then(() => {
          setIsAuthenticated(true);
          // Redirecionar para página de detalhes do envelope se logado
          if (envelope.envelope_key) {
            navigate(`/dashboard/my-signatures/${envelope.envelope_key}`);
          }
        })
        .catch(() => {
          setIsAuthenticated(false);
          // Mostrar modal se não logado
          setShowLoginModal(true);
        });
    }
  }, [envelope, navigate]);

  const handleLogin = () => {
    // Salvar URL atual para redirecionamento após login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    // Navegar para página de login
    navigate('/login');
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleAssinarClick = () => {
    if (envelope?.signature_type === 'icp') {
      // Bypass: permitir seguir sem clicar na posição (validação comentada por enquanto)
      // if (!signaturePosition) {
      //   setShowNoPositionModal(true);
      // } else {
      //   navigate(`/signature/${signerKey}/icp`);
      // }
      navigate(`/signature/${signerKey}/icp`);
    }
  };

  const handleCloseNoPositionModal = () => {
    setShowNoPositionModal(false);
  };

  const handleConfirmNoPosition = () => {
    setShowNoPositionModal(false);
    navigate(`/signature/${signerKey}/icp`);
  };

  return (
    <StyledRoot>
      <SignatureHeader 
        envelope={envelope} 
        signerKey={signerKey}
        isObserver={envelope?.is_observer || false}
        isAuthenticated={isAuthenticated}
        onAssinarClick={handleAssinarClick}
      />
      <Main>
        <>
          <Helmet>
              <title> Assinar </title>
          </Helmet>

          <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>

          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
          <Grid item xs={12} sm={12} md={10} lg={8}>
            <div style={{
              overflowY: "auto",
              height: 'calc(100vh - 200px)',
              minHeight: '400px',
              maxHeight: '800px',
              width: '100%'
            }}>
              {
              envelope ? 
              // Apresenta na tela o pdf presente na variavel envelope.envelope_url
              <PdfComp 
                pdfFile={envelope.envelope_url} 
                enableSignaturePlacement={envelope.signature_type === 'icp'} 
                onSignaturePositionChange={setSignaturePosition}
              />
              : loading ?
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
                  <CircularProgress />
                </Stack>
              :
                <Typography variant="h4" gutterBottom>
                  Nenhum documento encontrado
                </Typography>
                }
            </div>
          </Grid>
        </Grid>

          </Container>
        </> 
      </Main>

      {/* Modal de login para observadores não autenticados */}
      <Dialog open={showLoginModal} onClose={handleCloseModal}>
        <DialogTitle>Login Necessário</DialogTitle>
        <DialogContent>
          <Typography>
            Você precisa estar logado para acessar este documento como observador.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleLogin} variant="contained">
            Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: assinar ICP sem ter selecionado posição no PDF */}
      <Dialog open={showNoPositionModal} onClose={handleCloseNoPositionModal}>
        <DialogTitle>Posição da assinatura</DialogTitle>
        <DialogContent>
          <Typography>
            Você tem certeza de que não deseja selecionar a posição da assinatura? Se não for selecionada, o sistema adicionará sua assinatura automaticamente na última página.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoPositionModal}>Cancelar</Button>
          <Button onClick={handleConfirmNoPosition} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </StyledRoot>
      );
}

export default SignaturePage;
